import type { PrinterProfile } from "../lib/data";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
	Star,
	MapPin,
	Clock,
	Shield,
	Ruler,
	Printer,
	ChevronRight,
	Award,
} from "lucide-react";

interface PrinterCardProps {
	printer: PrinterProfile;
	distance?: number;
	onSelect: (id: string) => void;
	isSelected?: boolean;
}

export function PrinterCard({ printer, distance, onSelect, isSelected }: PrinterCardProps) {
	return (
		<Card
			className={`group cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/30 ${
				isSelected ? "border-primary shadow-lg ring-2 ring-primary/20" : ""
			}`}
			onClick={() => onSelect(printer.id)}
		>
			<CardContent className="p-4">
				<div className="flex gap-4">
					{/* Avatar / Photo */}
					<div className="relative flex-shrink-0">
						<div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-border">
							<Printer className="w-7 h-7 text-primary/70" />
						</div>
						{printer.verified && (
							<div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
								<Shield className="w-3 h-3 text-white" />
							</div>
						)}
					</div>

					{/* Info */}
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-2">
							<div>
								<h3 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
									{printer.name}
								</h3>
								<p className="text-xs text-muted-foreground mt-0.5">{printer.ownerName}</p>
							</div>
							<div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-950/30 px-2 py-0.5 rounded-md flex-shrink-0">
								<Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
								<span className="text-xs font-semibold">{printer.rating}</span>
								<span className="text-xs text-muted-foreground">({printer.reviewCount})</span>
							</div>
						</div>

						{/* Printer model & location */}
						<div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
							<span className="flex items-center gap-1">
								<Printer className="w-3 h-3" />
								{printer.printerModel}
							</span>
							{distance !== undefined && (
								<span className="flex items-center gap-1">
									<MapPin className="w-3 h-3" />
									{distance} mi
								</span>
							)}
							<span className="flex items-center gap-1">
								<Clock className="w-3 h-3" />
								{printer.responseTime}
							</span>
						</div>

						{/* Materials */}
						<div className="flex flex-wrap gap-1 mt-2">
							{printer.materials.slice(0, 4).map((mat) => (
								<Badge key={mat} variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
									{mat}
								</Badge>
							))}
							{printer.materials.length > 4 && (
								<Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
									+{printer.materials.length - 4}
								</Badge>
							)}
						</div>

						{/* Build size & reputation */}
						<div className="flex items-center justify-between mt-2">
							<div className="flex items-center gap-3 text-[11px] text-muted-foreground">
								<span className="flex items-center gap-1">
									<Ruler className="w-3 h-3" />
									{printer.maxBuildSize.x}x{printer.maxBuildSize.y}x{printer.maxBuildSize.z}mm
								</span>
								<span className="flex items-center gap-1">
									<Award className="w-3 h-3" />
									{printer.reputationScore}% rep
								</span>
							</div>
							<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
